/**
 * Applicants (HR): every employment application from the Jotform, the New Hire Checklist step by step, and the
 * command staff's view by division and certification.
 */
import { useState } from 'react';
import { APPLICANT_RESULTS, APPLICANT_STEPS } from '@civilos/domain';
import { api } from '../shared/api.ts';
import { useRouter } from '../shared/router.tsx';
import { Badge, Card, ErrorBox, Notice, PageHeader, Spinner, useAction, useLoad } from '../shared/ui.tsx';
import { at, day, EmptyState, KPI, when } from './common.tsx';
import { FileList, UploadButton } from './WorkFiles.tsx';
import { HookLinkCard } from './HookLink.tsx';

const DIV: Record<string, string> = { sheriff: 'Sheriff’s Office', detention: 'Detention' };
const RESULT: Record<string, string> = Object.fromEntries(APPLICANT_RESULTS.map((r) => [r.code, r.label]));
const certText = (c: boolean | null) => (c === true ? 'Certified' : c === false ? 'Non-certified' : 'Certification ?');

export function ApplicantsPage() {
  const { navigate } = useRouter();
  const [division, setDivision] = useState('');
  const [cert, setCert] = useState('');
  const [status, setStatus] = useState('open');
  const [q, setQ] = useState('');
  const r = useLoad(() => api.get<any>(`/api/applicants?division=${division}&certified=${cert}&status=${status}&q=${encodeURIComponent(q)}`), [division, cert, status, q]);
  const c = r.data?.counts;
  const seg = (val: string, set: (v: string) => void, opts: [string, string][], label: string) => (
    <div className="seg-ctl" role="tablist" aria-label={label}>
      {opts.map(([k, l]) => (
        <button key={k} type="button" role="tab" aria-selected={val === k} className={val === k ? 'on' : ''} onClick={() => set(k)}>
          {l}
        </button>
      ))}
    </div>
  );
  return (
    <div className="ops stack">
      <PageHeader
        crumb="HR"
        title={r.data?.dispatch ? 'Applicants: DMV and NCIC' : 'Applicants'}
        sub={r.data?.dispatch ? 'Applicants whose application is complete and need DMV and NCIC run. Mark each one done when it’s back.' : 'Employment applications from the Jotform and where each one stands on the New Hire Checklist.'}
        actions={
          r.data?.canManage && (
            <button className="btn primary" onClick={() => navigate(at('/hr/applicants/new'))}>
              Enter an application
            </button>
          )
        }
      />
      {c && (
        <div className="ops-kpis">
          <KPI n={c.sheriff_cert} label="Sheriff’s Office · certified" onClick={() => (setDivision('sheriff'), setCert('yes'), setStatus('open'))} on={division === 'sheriff' && cert === 'yes'} />
          <KPI n={c.sheriff_non} label="Sheriff’s Office · non-certified" onClick={() => (setDivision('sheriff'), setCert('no'), setStatus('open'))} on={division === 'sheriff' && cert === 'no'} />
          <KPI n={c.detention_cert} label="Detention · certified" onClick={() => (setDivision('detention'), setCert('yes'), setStatus('open'))} on={division === 'detention' && cert === 'yes'} />
          <KPI n={c.detention_non} label="Detention · non-certified" onClick={() => (setDivision('detention'), setCert('no'), setStatus('open'))} on={division === 'detention' && cert === 'no'} />
          {c.unsorted > 0 && <KPI n={c.unsorted} label="Division not set" tone="warn" />}
        </div>
      )}
      {r.data?.canManage && (
        <div className="row gap-sm wrap">
          {seg(division, setDivision, [['', 'Both'], ['sheriff', 'Sheriff’s Office'], ['detention', 'Detention']], 'Division')}
          {seg(cert, setCert, [['', 'All'], ['yes', 'Certified'], ['no', 'Non-certified']], 'Certification')}
          {seg(status, setStatus, [['open', 'Open'], ['hired', 'Hired'], ['closed', 'Closed'], ['all', 'All']], 'Status')}
          <input type="search" placeholder="Search name, position…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search applicants" style={{ maxWidth: 240 }} />
        </div>
      )}
      {r.error ? (
        <ErrorBox error={r.error} />
      ) : !r.data ? (
        <Spinner />
      ) : !r.data.applicants.length ? (
        <EmptyState icon="people" title={r.data.dispatch ? 'No applicants waiting on DMV / NCIC' : 'No applicants'} />
      ) : (
        <div className="card table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Applicant</th>
                <th>Position</th>
                {r.data.dispatch ? (
                  <>
                    <th>DOB</th>
                    <th>Driver’s license</th>
                  </>
                ) : (
                  <>
                    <th>Division</th>
                    <th>Progress</th>
                    <th>Next step</th>
                  </>
                )}
                <th>Received</th>
              </tr>
            </thead>
            <tbody>
              {r.data.applicants.map((a: any) => (
                <tr key={a.id} className="clickable" onClick={() => navigate(at(`/hr/applicants/${a.id}`))}>
                  <td>
                    <strong>{a.name}</strong>
                    <div className="small muted mono">{a.number}</div>
                  </td>
                  <td>{a.position ?? '—'}</td>
                  {r.data.dispatch ? (
                    <>
                      <td>{a.dob ?? '—'}</td>
                      <td className="mono">{a.dl ? `${a.dl} ${a.dl_state ?? ''}` : '—'}</td>
                    </>
                  ) : (
                    <>
                      <td>
                        {a.division ? DIV[a.division] : <span className="muted">—</span>}
                        <div className="small muted">{certText(a.certified)}</div>
                      </td>
                      <td>
                        <div className="ap-bar" aria-label={`${a.done} of ${a.total} steps`}>
                          <span style={{ width: `${(a.done / a.total) * 100}%` }} />
                        </div>
                        <span className="small muted">
                          {a.done} of {a.total}
                        </span>
                      </td>
                      <td>{a.result ? <Badge tone={a.result === 'hired' ? 'ok' : 'neutral'}>{RESULT[a.result]}</Badge> : a.next ? <span className="small">{a.next.label}</span> : <Badge tone="ok">Checklist done</Badge>}</td>
                    </>
                  )}
                  <td>{day(a.received_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {r.data?.canManage && <HookLinkCard title="Connect the employment application Jotform" settings="/api/applicants/settings" rotate="/api/applicants/settings/link" what="employment application" detail="Every application then shows up here with the New Hire Checklist started. Name, phone, email, date of birth, SSN, driver’s license, position and certification are read from the question names; everything else is kept with the application. The SSN is stored masked on screen." />}
    </div>
  );
}

const BASICS: [string, string, string?][] = [
  ['phone', 'Phone'],
  ['email', 'Email', 'email'],
  ['dob', 'Date of birth', 'date'],
  ['dl', 'Driver’s license #'],
  ['dlState', 'DL state'],
  ['position', 'Position applied for'],
];

function BasicsForm({ initial, onSave, busy, submit = 'Save' }: { initial: Record<string, any>; onSave: (v: Record<string, any>) => void; busy: boolean; submit?: string }) {
  const [v, setV] = useState<Record<string, any>>(initial);
  return (
    <form
      className="stack sm"
      onSubmit={(e) => {
        e.preventDefault();
        onSave(v);
      }}
    >
      <div className="grid-3">
        {'name' in initial && (
          <label>
            Name
            <input required value={v.name ?? ''} onChange={(e) => setV({ ...v, name: e.target.value })} />
          </label>
        )}
        {BASICS.map(([k, l, t]) => (
          <label key={k}>
            {l}
            <input type={t ?? 'text'} value={v[k] ?? ''} onChange={(e) => setV({ ...v, [k]: e.target.value })} />
          </label>
        ))}
        <label>
          SSN {initial.ssnMasked ? <span className="small muted">(now {initial.ssnMasked}; leave blank to keep)</span> : null}
          <input inputMode="numeric" autoComplete="off" placeholder="###-##-####" value={v.ssn ?? ''} onChange={(e) => setV({ ...v, ssn: e.target.value })} />
        </label>
        <label>
          Division
          <select value={v.division ?? ''} onChange={(e) => setV({ ...v, division: e.target.value || null })}>
            <option value="">—</option>
            <option value="sheriff">Sheriff’s Office</option>
            <option value="detention">Detention</option>
          </select>
        </label>
        <label>
          Certified?
          <select value={v.certified == null ? '' : v.certified ? 'yes' : 'no'} onChange={(e) => setV({ ...v, certified: e.target.value === '' ? null : e.target.value === 'yes' })}>
            <option value="">—</option>
            <option value="yes">Certified</option>
            <option value="no">Non-certified</option>
          </select>
        </label>
      </div>
      <div>
        <button className="btn primary" disabled={busy}>
          {submit}
        </button>
      </div>
    </form>
  );
}

export function NewApplicant() {
  const { navigate } = useRouter();
  const a = useAction();
  return (
    <div className="ops stack">
      <PageHeader crumb="Applicants" title="Enter an application" sub="For a paper application or one that came by email." />
      <ErrorBox error={a.error} />
      <Card title="Applicant">
        <BasicsForm initial={{ name: '' }} busy={a.busy} submit="Save" onSave={(v) => a.run(async () => navigate(at(`/hr/applicants/${(await api.post<{ id: string }>('/api/applicants', v)).id}`), true))} />
      </Card>
    </div>
  );
}

function StepRow({ id, s, st, canEdit, onDone }: { id: string; s: (typeof APPLICANT_STEPS)[number]; st: any; canEdit: boolean; onDone: () => void }) {
  const a = useAction();
  const [open, setOpen] = useState(false);
  const [v, setV] = useState<Record<string, string>>({ date: st?.date ?? '', at: st?.at ?? '', note: st?.note ?? '', sentOn: st?.sentOn ?? '', receivedFrom: st?.receivedFrom ?? '', receivedOn: st?.receivedOn ?? '' });
  const done = s.key === 'acadis' ? !!st?.receivedOn : !!(st?.date || st?.at);
  const scheduled = s.key === 'interview' || s.key === 'test_day';
  const save = (body: Record<string, unknown>) => a.run(async () => (await api.post(`/api/applicants/${id}/step`, { key: s.key, ...body }), setOpen(false), onDone()));
  const summary = st
    ? s.key === 'acadis'
      ? [st.sentOn && `Sent ${day(st.sentOn)}`, st.receivedOn && `received email from ${st.receivedFrom ?? '—'} on ${day(st.receivedOn)}`].filter(Boolean).join(' · ')
      : [st.date && (scheduled ? `For ${day(st.date)}` : day(st.date)), st.at, st.byName && `· ${st.byName}`].filter(Boolean).join(' ')
    : null;
  return (
    <li className={done ? 'on done' : st ? 'on part' : ''}>
      <span className="cf-box" aria-hidden>
        {done ? '✓' : ''}
      </span>
      <div className="cf-body">
        <strong>{s.label}</strong>
        {s.who === 'dispatch' && <span className="small muted"> · E-911</span>}
        {summary && <div className="small">{summary}</div>}
        {st?.note && <div className="small muted">{st.note}</div>}
        {open && (
          <div className="ap-step-form">
            {s.key === 'acadis' ? (
              <div className="grid-3">
                <label>
                  BG form sent on
                  <input type="date" value={v.sentOn} onChange={(e) => setV({ ...v, sentOn: e.target.value })} />
                </label>
                <label>
                  Received email from
                  <input value={v.receivedFrom} onChange={(e) => setV({ ...v, receivedFrom: e.target.value })} placeholder="Name / SCCJA" />
                </label>
                <label>
                  Received on
                  <input type="date" value={v.receivedOn} onChange={(e) => setV({ ...v, receivedOn: e.target.value })} />
                </label>
              </div>
            ) : (
              <div className="grid-3">
                <label>
                  {scheduled ? 'Scheduled for' : 'Date'}
                  <input type="date" value={v.date} onChange={(e) => setV({ ...v, date: e.target.value })} required={scheduled} />
                </label>
                {scheduled && (
                  <label>
                    Time
                    <input type="time" value={v.at} onChange={(e) => setV({ ...v, at: e.target.value })} />
                  </label>
                )}
              </div>
            )}
            <label>
              Note
              <input value={v.note} onChange={(e) => setV({ ...v, note: e.target.value })} />
            </label>
            <div className="row gap-sm">
              <button type="button" className="btn primary sm" disabled={a.busy} onClick={() => save(Object.fromEntries(Object.entries(v).filter(([, x]) => x)))}>
                Save
              </button>
              {st && (
                <button type="button" className="btn ghost sm" disabled={a.busy} onClick={() => save({ clear: true })}>
                  Clear this step
                </button>
              )}
              <button type="button" className="btn ghost sm" onClick={() => setOpen(false)}>
                Cancel
              </button>
            </div>
            <ErrorBox error={a.error} />
          </div>
        )}
      </div>
      {canEdit && !open && (
        <div className="cf-acts">
          {!st && !scheduled && s.key !== 'acadis' && (
            <button type="button" className="btn sm primary" disabled={a.busy} onClick={() => save({})}>
              Done today
            </button>
          )}
          <button type="button" className="btn sm" onClick={() => setOpen(true)}>
            {st ? 'Edit' : scheduled ? 'Schedule' : s.key === 'acadis' ? 'Enter dates' : 'Pick date'}
          </button>
        </div>
      )}
    </li>
  );
}

export function ApplicantDetail({ id }: { id: string }) {
  const r = useLoad(() => api.get<any>(`/api/applicants/${id}`), [id]);
  const a = useAction();
  const [ssn, setSsn] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  if (!r.data) return r.error ? <ErrorBox error={r.error} /> : <Spinner />;
  const { applicant: x, form, files, canManage, dispatch } = r.data;
  const setResult = (result: string | null) => {
    const note = result ? window.prompt(`${RESULT[result]} — note (optional)`) : null;
    if (result && note === null) return;
    a.run(async () => (await api.post(`/api/applicants/${id}/result`, { result, note: note || undefined }), r.reload()));
  };
  return (
    <div className="ops stack">
      <PageHeader
        crumb="Applicants"
        title={x.name}
        sub={`${x.number} · ${x.position ?? 'position not given'} · ${x.division ? DIV[x.division] : 'division not set'} · ${certText(x.certified)} · received ${when(x.received_at)}`}
        actions={x.result ? <Badge tone={x.result === 'hired' ? 'ok' : 'neutral'}>{RESULT[x.result]}</Badge> : x.next ? <Badge tone="warn">Next: {x.next.label}</Badge> : <Badge tone="ok">Checklist done</Badge>}
      />
      <ErrorBox error={a.error} />
      <div className="grid-2-cards">
        <Card title="New Hire Checklist">
          <ol className="cf-items">
            {APPLICANT_STEPS.filter((s) => !dispatch || s.key === 'dmv_ncic').map((s) => (
              <StepRow key={s.key} id={id} s={s} st={x.steps?.[s.key]} canEdit={!x.result && (canManage || (dispatch && s.key === 'dmv_ncic'))} onDone={r.reload} />
            ))}
          </ol>
        </Card>
        <Card
          title="Applicant"
          actions={
            canManage &&
            !editing && (
              <button className="btn sm" onClick={() => setEditing(true)}>
                Edit
              </button>
            )
          }
        >
          {editing ? (
            <BasicsForm
              initial={{ name: x.name, phone: x.phone, email: x.email, dob: x.dob, dl: x.dl, dlState: x.dl_state, position: x.position, division: x.division, certified: x.certified, ssnMasked: x.ssn }}
              busy={a.busy}
              onSave={(v) => a.run(async () => (await api.post(`/api/applicants/${id}`, { ...v, ssn: v.ssn || undefined }), setEditing(false), r.reload()))}
            />
          ) : (
            <dl className="kv">
              {canManage && (
                <>
                  <dt>Phone</dt>
                  <dd>{x.phone ? <a href={`tel:${x.phone.replace(/[^\d+]/g, '')}`}>{x.phone}</a> : '—'}</dd>
                  <dt>Email</dt>
                  <dd>{x.email ? <a href={`mailto:${x.email}`}>{x.email}</a> : '—'}</dd>
                </>
              )}
              <dt>Date of birth</dt>
              <dd>{x.dob ?? '—'}</dd>
              {canManage && (
                <>
                  <dt>SSN</dt>
                  <dd className="mono">
                    {ssn ?? x.ssn ?? '—'}{' '}
                    {x.ssn && !ssn && (
                      <button type="button" className="btn ghost sm" onClick={() => a.run(async () => setSsn((await api.get<{ ssn: string }>(`/api/applicants/${id}/ssn`)).ssn))}>
                        Show (logged)
                      </button>
                    )}
                  </dd>
                </>
              )}
              <dt>Driver’s license</dt>
              <dd className="mono">{x.dl ? `${x.dl} ${x.dl_state ?? ''}` : '—'}</dd>
            </dl>
          )}
          {canManage && (
            <div className="stack sm" style={{ marginTop: 12 }}>
              <div className="small strong">Result</div>
              <div className="row gap-sm wrap">
                {APPLICANT_RESULTS.map((o) => (
                  <button key={o.code} type="button" className={`btn sm ${x.result === o.code ? 'primary' : ''}`} disabled={a.busy} onClick={() => setResult(o.code)}>
                    {o.label}
                  </button>
                ))}
                {x.result && (
                  <button type="button" className="btn ghost sm" disabled={a.busy} onClick={() => setResult(null)}>
                    Reopen
                  </button>
                )}
              </div>
              {x.result_note && <p className="small muted">{x.result_note}</p>}
            </div>
          )}
        </Card>
      </div>
      {canManage && (
        <Card title="Documents" actions={<UploadButton url={`/api/applicants/${id}/files`} onDone={r.reload} />}>
          <FileList files={files} />
        </Card>
      )}
      {canManage && form.length > 0 && (
        <Card title="From the application form">
          <details>
            <summary className="small">Show all {form.length} answers</summary>
            <dl className="kv">
              {form.map(([l, v]: [string, string], i: number) => (
                <div key={i} style={{ display: 'contents' }}>
                  <dt>{l}</dt>
                  <dd className="pre">{v}</dd>
                </div>
              ))}
            </dl>
          </details>
        </Card>
      )}
      {dispatch && <Notice tone="info">Run DMV and NCIC on this applicant, then mark the step done. Only HR sees the rest of the application.</Notice>}
    </div>
  );
}
